import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './UnitReorderList.module.css';

function formatDateRange(startDate, endDate) {
  if (!startDate || !endDate) return '';
  const opts = { month: 'short', day: 'numeric' };
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  return `${start.toLocaleDateString('en-US', opts)} \u2013 ${end.toLocaleDateString('en-US', opts)}`;
}

function SortableUnitCard({ unit, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: unit.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.unitCard} ${isDragging ? styles.dragging : ''}`}
    >
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        {'\u2807'}
      </div>
      <div className={styles.unitContent}>
        <div className={styles.unitHeader}>
          <span className={styles.unitNumber}>Unit {index + 1}</span>
          <span className={styles.unitWeeks}>{unit.weeks} wks</span>
        </div>
        <div className={styles.unitName}>{unit.title}</div>
        {(unit.startDate || unit.endDate) && (
          <div className={styles.unitDates}>
            {formatDateRange(unit.startDate, unit.endDate)}
          </div>
        )}
        {unit.description && (
          <div className={styles.unitDescription}>{unit.description}</div>
        )}
        {unit.standards && unit.standards.length > 0 && (
          <div className={styles.unitStandards}>
            {unit.standards.map((s, j) => (
              <span key={j} className={styles.standardBadge}>{s}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UnitReorderList({ units, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = units.findIndex((u) => u.id === active.id);
    const newIndex = units.findIndex((u) => u.id === over.id);
    const reordered = arrayMove(units, oldIndex, newIndex);
    onReorder(reordered);
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={units.map((u) => u.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={styles.list}>
          {units.map((unit, index) => (
            <SortableUnitCard key={unit.id} unit={unit} index={index} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default UnitReorderList;
